import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import { api } from './constants';
import Navbar from './components/Navbar';
import Home from './pages/Home';
import Catalog from './pages/Catalog';
import Cart from './pages/Cart';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Admin from './pages/Admin';
import ProductDetail from './pages/ProductDetail';
import Contact from './pages/Contact';
import Orders from './pages/Orders';
import OrderDetail from './pages/OrderDetail';
import Profile from './pages/Profile';
import Scans from './pages/Scans';
import PrintingServices from './pages/PrintingServices';
import { Product } from './types';

const AppContent: React.FC = () => {
  const { isAuthenticated, user } = useAuth();
  const [currentPage, setCurrentPage] = useState('home');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  // Deep Linking Support
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const productId = params.get('p');
    
    if (productId) {
      const loadDeepLink = async () => {
        try {
          const allProducts = await api.getProducts();
          const product = allProducts.find((p: Product) => p.id === productId);
          if (product) {
            setSelectedProduct(product);
            setCurrentPage('product-detail');
            // Clean up URL without refreshing
            window.history.replaceState({}, '', window.location.pathname);
          }
        } catch (err) {
          console.error('Failed to load deep linked product', err);
        }
      };
      loadDeepLink();
    }
  }, []);

  const navigateToProduct = (product: Product) => {
    setSelectedProduct(product);
    setCurrentPage('product-detail');
  };

  const navigateToOrder = (orderId: string) => {
    setSelectedOrderId(orderId);
    setCurrentPage('order-detail');
  };

  const handleNavigate = (page: string) => {
    if (page.startsWith('order-detail-')) {
      const id = page.replace('order-detail-', '');
      setSelectedOrderId(id);
      setCurrentPage('order-detail');
    } else {
      setCurrentPage(page);
    }
  };

  const renderPage = () => {
    switch (currentPage) {
      case 'home':
        return <Home onNavigate={handleNavigate} />;
      case 'catalog-lab':
        return <Catalog type="LAB" onNavigate={handleNavigate} onSelectProduct={navigateToProduct} />;
      case 'catalog-clinic':
        return <Catalog type="CLINIC" onNavigate={handleNavigate} onSelectProduct={navigateToProduct} />;
      case 'product-detail':
        return selectedProduct ? (
          <ProductDetail 
            product={selectedProduct} 
            onNavigate={handleNavigate} 
          />
        ) : <Home onNavigate={handleNavigate} />;
      case 'contact':
        return <Contact />;
      case 'cart':
        return <Cart onNavigate={handleNavigate} onOrderCreated={navigateToOrder} onSelectProduct={navigateToProduct} />;
      case 'orders':
        return <Orders onNavigate={handleNavigate} />;
      case 'order-detail':
        return selectedOrderId ? (
            <OrderDetail orderId={selectedOrderId} onNavigate={handleNavigate} />
        ) : <Orders onNavigate={handleNavigate} />;
      case 'login':
        return <Login onNavigate={handleNavigate} />;
      case 'forgot-password':
        return <ForgotPassword onNavigate={handleNavigate} />;
      case 'profile':
        return <Profile onNavigate={handleNavigate} />;
      case 'scans':
        return <Scans />;
      case 'admin':
        return <Admin onNavigate={handleNavigate} />;
      case 'printing-services':
        return <PrintingServices onNavigate={handleNavigate} />;
      default:
        return <Home onNavigate={handleNavigate} />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col selection:bg-blue-100 animate-fade-in">
      <Navbar onNavigate={handleNavigate} currentPage={currentPage} />
      
      <main className="flex-grow">
        {renderPage()}
      </main>
      
      <footer className="bg-white/50 backdrop-blur-sm border-t border-slate-100 mt-20 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="col-span-2">
              <div className="flex items-center space-x-3 text-blue-600 text-2xl font-black mb-6">
                <div className="w-12 h-12 bg-blue-50 rounded-2xl overflow-hidden flex items-center justify-center border border-blue-100 shadow-sm">
                  <img 
                    src="https://images.unsplash.com/photo-1533738363-b7f9aef128ce?auto=format&fit=crop&w=80&q=80" 
                    alt="Piggy Dental Logo" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <span className="tracking-tight text-slate-900 uppercase">Piggy Dental</span>
              </div>
              <p className="text-slate-500 max-w-xs leading-relaxed font-medium">
                Vancouver's premier destination for high-precision dental laboratory infrastructure and clinical excellence.
              </p>
            </div>
            <div>
              <h4 className="font-black text-slate-900 mb-6 uppercase tracking-widest text-xs">Navigation</h4>
              <ul className="space-y-3 text-slate-500 font-semibold text-sm">
                <li><button onClick={() => handleNavigate('catalog-lab')} className="hover:text-blue-600 transition-colors">Lab Solutions</button></li>
                <li><button onClick={() => handleNavigate('catalog-clinic')} className="hover:text-blue-600 transition-colors">Clinic Solutions</button></li>
                <li><button onClick={() => handleNavigate('contact')} className="hover:text-blue-600 transition-colors">Enterprise Sales</button></li>
                <li><button onClick={() => {
                  if (isAuthenticated) {
                    handleNavigate(user?.role === 'ADMIN' ? 'admin' : 'profile');
                  } else {
                    handleNavigate('login');
                  }
                }} className="hover:text-blue-600 transition-colors">Business Dashboard</button></li>
              </ul>
            </div>
            <div>
              <h4 className="font-black text-slate-900 mb-6 uppercase tracking-widest text-xs">Contact</h4>
              <ul className="space-y-3 text-slate-500 font-semibold text-sm">
                <li className="flex items-center">
                  <span className="mr-2 opacity-50">📍</span> 1230 W Georgia St, BC
                </li>
                <li className="flex items-center">
                  <span className="mr-2 opacity-50">✉️</span> info@piggyll.com
                </li>
                <li className="flex items-center">
                  <span className="mr-2 opacity-50">📞</span> +1 (604) 555-0199
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-100 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center text-[10px] font-black uppercase tracking-[0.2em] text-slate-300">
            <div>&copy; {new Date().getFullYear()} Piggy Dental Supply Ltd.</div>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <span className="hover:text-blue-600 cursor-pointer transition-colors">Terms</span>
              <span className="hover:text-blue-600 cursor-pointer transition-colors">Privacy</span>
              <span className="hover:text-blue-600 cursor-pointer transition-colors">Compliance</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <LanguageProvider>
      <AuthProvider>
        <CartProvider>
          <AppContent />
        </CartProvider>
      </AuthProvider>
    </LanguageProvider>
  );
};

export default App;
